import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Video, Pencil, Trash2, X, Users, Building2, RotateCcw, AlertTriangle } from "lucide-react";
import { api, isUnreachableError } from "../../lib/api";
import { showServiceUnavailable } from "../../components/ServiceUnavailableToast";

const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const isExpired = (iso) => iso && new Date(iso).getTime() < Date.now();

function MeetingModal({ initial, clients, users, onSave, onClose, saving }) {
    const [title, setTitle] = useState(initial?.title || "");
    const [startLocal, setStartLocal] = useState(() => {
        if (initial?.datetime_start) {
            const d = new Date(initial.datetime_start);
            const pad = (n) => String(n).padStart(2, "0");
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        }
        const d = new Date(Date.now() + 60 * 60 * 1000);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    });
    const [duration, setDuration] = useState(() => {
        if (initial?.datetime_start && initial?.datetime_end) {
            const s = new Date(initial.datetime_start), e = new Date(initial.datetime_end);
            return Math.max(15, Math.round((e - s) / 60000));
        }
        return 60;
    });
    const [description, setDescription] = useState(initial?.description || "");
    const [participants, setParticipants] = useState((initial?.participants_internal || []).map(p => p.id));
    const [clientId, setClientId] = useState(initial?.client?.id || "");

    const toggleP = (uid) => {
        setParticipants(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
    };

    const submit = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (saving) return;
        if (!title.trim()) { toast.error("Titolo richiesto"); return; }
        if (!startLocal) { toast.error("Data/ora inizio richiesta"); return; }
        const start = new Date(startLocal);
        const end = new Date(start.getTime() + duration * 60000);
        onSave({
            title: title.trim(),
            datetime_start: start.toISOString(),
            datetime_end: end.toISOString(),
            description: description.trim() || null,
            participants_internal: participants,
            client_id: clientId || null,
        });
    };

    return (
        <div className="lt-modal-backdrop" onClick={onClose}>
            <div className="lt-modal" onClick={(e) => e.stopPropagation()} data-testid="meeting-modal" style={{ maxWidth: 640 }}>
                <div className="lt-modal__head">
                    <h2 className="lt-modal__title">{initial ? "Modifica riunione" : "Nuova riunione"}</h2>
                    <button className="lt-icon-btn" onClick={onClose} aria-label="Chiudi"><X size={16} /></button>
                </div>
                <form onSubmit={submit} method="post" action="#" noValidate className="lt-scad-form" data-testid="meeting-form">
                    <div className="lt-form-row">
                        <label className="lt-label">Titolo</label>
                        <input className="lt-input" value={title} onChange={(e) => setTitle(e.target.value)} required minLength={2} maxLength={200} data-testid="meeting-title" />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
                        <div className="lt-form-row">
                            <label className="lt-label">Data/ora inizio</label>
                            <input type="datetime-local" className="lt-input" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} required data-testid="meeting-start" />
                        </div>
                        <div className="lt-form-row">
                            <label className="lt-label">Durata (min)</label>
                            <input type="number" min={15} max={480} className="lt-input" value={duration} onChange={(e) => setDuration(parseInt(e.target.value || "60", 10))} data-testid="meeting-duration" />
                        </div>
                    </div>
                    <div className="lt-form-row">
                        <label className="lt-label">Descrizione</label>
                        <textarea className="lt-textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} data-testid="meeting-description" />
                    </div>
                    <div className="lt-form-row">
                        <label className="lt-label">Cliente (opzionale)</label>
                        <select className="lt-input" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="meeting-client">
                            <option value="">— Nessun cliente —</option>
                            {(clients || []).map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                        </select>
                    </div>
                    <div className="lt-form-row">
                        <label className="lt-label">Partecipanti interni</label>
                        <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #EFEFEC", padding: 8 }} data-testid="meeting-participants">
                            {(users || []).length === 0 && <div style={{ color: "#8A8F88", fontSize: 13 }}>Nessun utente disponibile</div>}
                            {(users || []).map(u => (
                                <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: 4, cursor: "pointer" }}>
                                    <input type="checkbox" checked={participants.includes(u.id)} onChange={() => toggleP(u.id)} data-testid={`meeting-p-${u.id}`} />
                                    <span>{u.name}</span>
                                    <span style={{ color: "#8A8F88", fontSize: 12 }}>{u.email}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="lt-form-actions">
                        <div style={{ flex: 1 }} />
                        <button type="button" className="lt-btn-secondary" onClick={onClose} data-testid="meeting-cancel">Annulla</button>
                        <button type="submit" className="lt-btn-primary" disabled={saving} onClick={submit} data-testid="meeting-save">{saving ? "Salvataggio…" : "Salva"}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function Riunioni() {
    const navigate = useNavigate();
    const [filter, setFilter] = useState("upcoming");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [saving, setSaving] = useState(false);

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/internal/meetings", { params: { filter, limit: 100 } });
            setItems(data.items || []);
        } catch (err) {
            if (isUnreachableError(err)) {
                showServiceUnavailable(() => fetchList());
            } else {
                toast.error("Errore caricamento");
            }
        } finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { fetchList(); }, [fetchList]);
    useEffect(() => {
        api.get("/internal/clients", { params: { status: "active", page_size: 200 } }).then(({ data }) => setClients(data.items || [])).catch(() => {});
        api.get("/internal/settings/internal-users").then(({ data }) => setUsers(data.items || data)).catch(() => setUsers([]));
    }, []);

    const doSave = async (payload) => {
        setSaving(true);
        try {
            if (editItem) {
                await api.patch(`/internal/meetings/${editItem.id}`, payload);
                toast.success("Riunione aggiornata");
            } else {
                await api.post("/internal/meetings", payload);
                toast.success("Riunione creata");
            }
            setShowModal(false); setEditItem(null); fetchList();
        } catch (err) {
            if (isUnreachableError(err)) {
                showServiceUnavailable(() => doSave(payload));
            } else {
                toast.error(err?.response?.data?.detail || "Errore");
            }
        } finally { setSaving(false); }
    };

    const doDelete = async (m) => {
        if (!window.confirm(`Eliminare la riunione "${m.title}"?`)) return;
        try { await api.delete(`/internal/meetings/${m.id}`); toast.success("Riunione eliminata"); fetchList(); }
        catch (err) {
            if (isUnreachableError(err)) showServiceUnavailable(() => doDelete(m));
            else toast.error(err?.response?.data?.detail || "Errore");
        }
    };

    const doReopen = async (m) => {
        try {
            const { data } = await api.post(`/internal/meetings/${m.id}/reopen`);
            const until = new Date(data.datetime_end).toLocaleTimeString(
                "it-IT", { hour: "2-digit", minute: "2-digit" },
            );
            toast.success(`Riunione riaperta fino a ${until}`);
            fetchList();
        } catch (err) {
            if (isUnreachableError(err)) showServiceUnavailable(() => doReopen(m));
            else if (err?.response?.status === 403) toast.error("Non autorizzato");
            else toast.error(err?.response?.data?.detail || "Errore riapertura");
        }
    };

    return (
        <div className="lt-page" data-testid="riunioni-page">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <h1 className="lt-h1" style={{ margin: 0 }}>Riunioni</h1>
                <div style={{ flex: 1 }} />
                <div className="lt-segmented" data-testid="riunioni-filter">
                    <button type="button" className={"lt-segmented__btn" + (filter === "upcoming" ? " is-active" : "")} onClick={() => setFilter("upcoming")}>In arrivo</button>
                    <button type="button" className={"lt-segmented__btn" + (filter === "past" ? " is-active" : "")} onClick={() => setFilter("past")}>Passate</button>
                </div>
                <button type="button" className="lt-btn-primary" onClick={() => { setEditItem(null); setShowModal(true); }} data-testid="riunioni-new-btn">
                    <Plus size={14} /> Nuova riunione
                </button>
            </div>

            <div className="lt-card" style={{ padding: 0 }}>
                <table className="lt-table" data-testid="riunioni-table">
                    <thead>
                        <tr>
                            <th>Titolo</th>
                            <th style={{ width: 200 }}>Quando</th>
                            <th style={{ width: 180 }}>Cliente</th>
                            <th style={{ width: 100 }}>Partec.</th>
                            <th style={{ width: 160 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? <tr><td colSpan={5} style={{ padding: 24, color: "#8A8F88" }}>Caricamento…</td></tr>
                            : items.length === 0 ? <tr><td colSpan={5} style={{ padding: 24, color: "#8A8F88" }} data-testid="riunioni-empty">Nessuna riunione.</td></tr>
                                : items.map(m => {
                                    const expired = isExpired(m.datetime_end);
                                    return (
                                    <tr key={m.id} data-testid={`riunione-row-${m.id}`}>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/app/riunioni/${m.id}`)}
                                                    data-testid={`riunione-title-link-${m.id}`}
                                                    style={{
                                                        fontWeight: 500, color: "#181B20",
                                                        background: "transparent", border: "none",
                                                        padding: 0, cursor: "pointer", textAlign: "left",
                                                        textDecoration: "underline",
                                                        textDecorationColor: "transparent",
                                                        textUnderlineOffset: 3,
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.textDecorationColor = "#181B20"}
                                                    onMouseLeave={(e) => e.currentTarget.style.textDecorationColor = "transparent"}
                                                >
                                                    {m.title}
                                                </button>
                                                {expired && (
                                                    <span data-testid={`riunione-expired-${m.id}`}
                                                          style={{
                                                            display: "inline-flex", alignItems: "center", gap: 4,
                                                            padding: "2px 8px", borderRadius: 999,
                                                            background: "#FEE2E2", color: "#7F1D1D",
                                                            fontFamily: "'IBM Plex Mono', monospace",
                                                            fontSize: 10, fontWeight: 600,
                                                            letterSpacing: "0.08em", textTransform: "uppercase",
                                                          }}>
                                                        <AlertTriangle size={10} /> Scaduta
                                                    </span>
                                                )}
                                            </div>
                                            {m.description && <div style={{ color: "#8A8F88", fontSize: 12 }}>{m.description.slice(0, 80)}</div>}
                                        </td>
                                        <td style={{ fontSize: 13 }}>{fmt(m.datetime_start)}</td>
                                        <td>{m.client ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Building2 size={12} /> {m.client.company_name}</span> : <span style={{ color: "#8A8F88" }}>—</span>}</td>
                                        <td style={{ color: "#5F6560" }}><Users size={12} /> {(m.participants_internal || []).length}</td>
                                        <td style={{ textAlign: "right" }}>
                                            {expired ? (
                                                <button type="button" className="lt-btn-primary"
                                                        style={{ fontSize: 12, padding: "4px 10px" }}
                                                        onClick={() => doReopen(m)}
                                                        data-testid={`meeting-reopen-${m.id}`}>
                                                    <RotateCcw size={12} /> Riapri
                                                </button>
                                            ) : (
                                                <button type="button" className="lt-btn-primary"
                                                        style={{ fontSize: 12, padding: "4px 10px" }}
                                                        onClick={() => navigate(`/app/riunioni/${m.id}/join`)}
                                                        data-testid={`meeting-join-${m.id}`}>
                                                    <Video size={12} /> Partecipa
                                                </button>
                                            )}
                                            <button type="button" className="lt-icon-btn" onClick={() => { setEditItem(m); setShowModal(true); }} data-testid={`meeting-edit-${m.id}`}><Pencil size={14} /></button>
                                            <button type="button" className="lt-icon-btn" onClick={() => doDelete(m)} data-testid={`meeting-delete-${m.id}`}><Trash2 size={14} /></button>
                                        </td>
                                    </tr>
                                    );
                                })}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <MeetingModal
                    initial={editItem}
                    clients={clients}
                    users={users}
                    saving={saving}
                    onSave={doSave}
                    onClose={() => { setShowModal(false); setEditItem(null); }}
                />
            )}
        </div>
    );
}
